import { useEffect, useState } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";
import { calendarDateInAccra } from "../../predictions/sessionDate.ts";
import { marketLabel, selectionLabel } from "../../exchange/market-format.ts";
import { FpHead, FpSkeleton, FpAwait, FpDisclaimer, FpIcon } from "../football/_fpkit.tsx";
import "../../styles/powerball-premium.css";
import "./under-dogs.css";

interface Leg {
  fixture_id: string; home_team: string; away_team: string; league: string;
  kickoff_at: string; market: string; odds: number; outcome: string | null;
}
interface Board {
  session_date: string;
  slip: { id: string; status: string; combined_odds: number; selections: Leg[]; result: string | null; published_at: string } | null;
}
const today = () => calendarDateInAccra(new Date())!;
const kickoff = (value: string) => new Date(value).toLocaleString("en-GB", {
  timeZone: "Africa/Accra", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});

export function UnderDogsPage(): JSX.Element {
  const [currentDay, setCurrentDay] = useState(today);
  const [historyDate, setHistoryDate] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const timer = window.setInterval(() => setCurrentDay(today()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const date = historyDate ?? currentDay;
  const board = useRpcQuery<Board>("get_under_dogs_board", {
    args: { p_session_date: date }, staleTime: 30_000, refetchInterval: 60_000,
  });
  const slip = board.data?.slip;
  const copy = async () => {
    if (!slip) return;
    const lines = slip.selections.map((l, i) => `${i + 1}. ${l.home_team} vs ${l.away_team}: ${selectionLabel(l.market, l.market, l)} @${Number(l.odds).toFixed(2)} (${kickoff(l.kickoff_at)} Accra)`);
    try {
      await navigator.clipboard.writeText([`STRIKZA UNDER DOGS - ${date}`, ...lines, `Combined odds: ${Number(slip.combined_odds).toFixed(2)}`].join("\n"));
      setCopied(true);
    } catch { setCopied(false); }
  };
  return <div className="fp under-dogs">
    <FpHead kicker="FOOTBALL" badge="DAILY" title="UNDER DOGS" />
    <div className="under-dogs-toolbar">
      <label>Ticket date <input aria-label="Ticket date" type="date" value={date} max={currentDay}
        onChange={e => { if (e.target.value) { setHistoryDate(e.target.value === currentDay ? null : e.target.value); setCopied(false); } }} /></label>
      <button type="button" onClick={() => { setHistoryDate(null); setCopied(false); }} disabled={historyDate === null}>Today</button>
      <button type="button" className="under-dogs-icon" aria-label="Refresh ticket" title="Refresh ticket" onClick={() => void board.refetch()} disabled={board.isFetching}><FpIcon name="refresh" /></button>
      {slip && <button type="button" onClick={() => void copy()}>{copied ? "Copied" : "Copy selections"}</button>}
    </div>
    {board.isLoading ? <FpSkeleton rows={4} /> : board.error ?
      <FpAwait kicker="UNDER DOGS" title="Ticket unavailable" body="We could not load this ticket. Please try refreshing." icon="info" ghosts={0} /> : !slip ?
      <FpAwait kicker="UNDER DOGS" title="No published ticket for this date" body="The next ticket will appear here when published." icon="clock" ghosts={0} /> :
      <section className="pb-prem-slip">
        <div className="pb-prem-slip-header"><h2 className="pb-prem-slip-title">{date}</h2><span className="pb-prem-slip-meta">{slip.result ?? slip.status}</span></div>
        <div className="pb-prem-combined">{Number(slip.combined_odds).toLocaleString("en-GH", { maximumFractionDigits: 2 })}</div>
        <div className="pb-prem-combined-sub">Combined odds / {slip.selections.length} selections / {new Set(slip.selections.map(l => l.league)).size} leagues</div>
        <div className="pb-prem-legs">{slip.selections.map((leg, index) => <article key={leg.fixture_id} className={`pb-prem-leg layer-foundation ${leg.outcome === "win" ? "tone-won" : leg.outcome === "loss" ? "tone-lost" : ""}`}>
          <div className="pb-prem-leg-num">Leg {String(index + 1).padStart(2, "0")}{leg.outcome ? ` / ${leg.outcome}` : ""}</div>
          <div className="pb-prem-leg-match">{leg.home_team} vs {leg.away_team}</div>
          <div className="pb-prem-leg-context">{leg.league} / {kickoff(leg.kickoff_at)} Accra</div>
          <div className="pb-prem-leg-pickline">{marketLabel(leg.market)} / <b>{selectionLabel(leg.market, leg.market, leg)}</b></div>
          <div className="pb-prem-leg-metrics"><span className="pb-prem-leg-odds">{Number(leg.odds).toFixed(2)}</span></div>
        </article>)}</div>
      </section>}
    <FpDisclaimer />
  </div>;
}
